/**
 * 运行时 <head> meta 工具（DOM 操作，幂等）。
 *
 * 供动态路由页面（文章页等）自行设置 title/description/OG，
 * 因为这些页面的内容来自 manifest（非 i18n 字典），Seo 组件的 dynamic 分支会跳过 title/description。
 * 与 Seo 组件用同样的 upsert 模式，保证幂等。
 */

/** 幂等设置一个 meta 标签（按 name 或 property 匹配）。 */
function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * 设置文章页的 title / description / OG / Twitter meta。
 * @param title 文章标题
 * @param description 文章描述
 * @param url 文章绝对 URL（含语言前缀）
 * @param imageUrl og:image / twitter:image 的绝对 URL（社交平台不解析相对路径；
 *                 缺省用站点默认分享图）
 */
export function setArticleMeta(
  title: string,
  description: string,
  url: string,
  imageUrl = "https://tsuvi.com/og-image.png",
): void {
  document.title = title;
  upsertMeta("name", "description", description);
  upsertMeta("property", "og:title", title);
  upsertMeta("property", "og:description", description);
  upsertMeta("property", "og:url", url);
  upsertMeta("property", "og:type", "article");
  upsertMeta("property", "og:image", imageUrl);
  // 文章内容仅英文（guides/use-cases/blog）：locale 固定 en_US，
  // 防止 SPA 从 /:lang 页面客户端切换过来时残留上一页的 og:locale
  upsertMeta("property", "og:site_name", "Tsuvi");
  upsertMeta("property", "og:locale", "en_US");
  upsertMeta("name", "twitter:card", "summary_large_image");
  upsertMeta("name", "twitter:title", title);
  upsertMeta("name", "twitter:description", description);
  upsertMeta("name", "twitter:image", imageUrl);
}

/**
 * 幂等设置 <link rel=canonical>。
 * 供博客等 /:lang 之外的动态路由自管（Seo 组件只挂载在 /:lang 布局下）。
 */
export function setCanonical(url: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

/**
 * 清空 hreflang alternate 链接。
 * 从 /:lang 页面跨到根级路由（/blog 等）时，Seo 组件卸载不清理旧
 * hreflang，会残留指向无关语言版本的 alternate，需在此清除。
 */
export function clearAlternateLinks(): void {
  document.head
    .querySelectorAll('link[rel="alternate"][hreflang]')
    .forEach((el) => el.remove());
}

/**
 * 注入 <meta name="robots" content="noindex">。
 * 供 404 / 内容不存在的兜底状态用：服务端已对未知路径返回 404 + noindex，
 * 客户端路由（不落网络请求）进入同样状态时补齐 noindex 信号。
 */
export function setNoindex(): void {
  upsertMeta("name", "robots", "noindex");
}

/** 移除 noindex（离开兜底状态后恢复可索引；正常页面本就没有该 meta）。 */
export function clearNoindex(): void {
  document.head
    .querySelectorAll('meta[name="robots"][content="noindex"]')
    .forEach((el) => el.remove());
}
